"use client";

import { CountryInfo, VisaTopCardItem } from "@/store/types/countryTypes";
import React from "react";
import Image from "next/image";
import { Clock, Handshake, RotateCcw } from "lucide-react";

const renderIcon = (card: VisaTopCardItem) => {
  if (card.iconImage) {
    return (
      <div className="relative w-7 h-7 shrink-0">
        <Image src={card.iconImage} alt={card.title} fill className="object-contain" />
      </div>
    );
  }

  return <Clock className="w-7 h-7 text-[#1C2233]" />;
};

const CountryPostStudyWorkVisa: React.FC<{ country: CountryInfo }> = ({ country }) => {
  const sectionData = country?.postStudyWorkVisa;

  const topCards = sectionData?.topCards || [];
  const timelineSteps = sectionData?.timelineSteps || [];

  if (topCards.length === 0 && timelineSteps.length === 0) {
    return null;
  }

  const heading = sectionData?.heading || "Post Study Work Visa";
  const subheading =
    sectionData?.subheading ||
    `The Graduate Route lets international students stay back and work in ${country.countryName} after finishing their degree.`;

  return (
    <div className="w-full my-12 space-y-8">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2233] ff_poppins">
          {heading}
        </h2>
        {subheading && (
          <p className="mt-1.5 text-xs sm:text-sm text-[#667085] ff_poppins">
            {subheading}
          </p>
        )}
      </div>

      {/* TOP HIGHLIGHT CARDS */}
      {topCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {topCards.map((card, idx) => (
            <div
              key={idx}
              className="bg-white rounded-[18px] border border-[#E0D8FF] p-6 shadow-sm hover:shadow-md transition-shadow space-y-3"
            >
              <div className="p-3 w-fit rounded-xl bg-[#F4F0FF] border border-[#E6DDFF]">
                {renderIcon(card)}
              </div>
              <h3 className="text-base font-bold text-[#1C2233] ff_poppins">
                {card.title}
              </h3>
              <p className="text-xs sm:text-sm text-[#525B73] ff_poppins leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* VERTICAL TIMELINE */}
      {timelineSteps.length > 0 && (
        <div className="pt-4 px-2 sm:px-4">
          <div className="relative border-l-2 border-[#2B7FFF] ml-3 sm:ml-4 space-y-8">
            {timelineSteps.map((step, idx) => (
              <div key={idx} className="relative pl-6 sm:pl-8 group">
                {/* Dot */}
                <div className="absolute -left-[9px] top-0.5 w-4 h-4 rounded-full bg-[#2B7FFF] border-4 border-white shadow-xs" />

                <div className="space-y-1">
                  <h4 className="text-base sm:text-lg font-bold text-[#1C2233] ff_poppins">
                    {step.stepNumber || idx + 1}. {step.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-[#525B73] ff_poppins leading-relaxed max-w-3xl">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryPostStudyWorkVisa;

